import URLX, { relativeUrl } from '../Url';
import localDb from '../local-db';
import { isBrowser } from '../device-type';
import { supportAsyncStorage, isServiceWorker } from '../device-type';

const maxCacheSize = 50;

const storage = localDb('cache');

export type CacheItem = Scope.IScopeModel & {
  url: string;
  componentName: string;
  currentTheme: string;
  meta: Scope.MetaInfo;
  $cache: 'full' | 'partial';
};

let allCachedUrls: string[] | null = null;
function getAllCachedUrls() {
  if (!allCachedUrls) {
    storage.getItem<string[]>(urlsByDateKey).then(urls => (allCachedUrls = urls || []));
  }
  return allCachedUrls as string[];
}

// We keep a small cache in memory of full pages so that we can respond quicker
// for the pages the user just viewed. This is mainly because when you go back
// in the browser history, the browser will restore scroll position and if we
// don't respond fast enough from the cache we haven't rendered the page when
// the browser restores scroll position. Which means that the scroll position
// will first jump, then we render, then the scroll position changes again.
const fastCache: Cache = {};
let fastCacheUrls: string[] = [];
const MAX_FAST_CACHE_SIZE = 10;

type Cache = { [url: string]: CacheItem };

export function isCacheItem(item: any): item is CacheItem {
  return !!(item && item.url && item.$cache && item.componentName && item.meta);
}

export function getCacheItem(url: URLX): Promise<CacheItem | null> {
  if (!supportAsyncStorage) {
    return Promise.resolve(null);
  }
  const key = relativeUrl(url);
  if (fastCache[key]) {
    console.info('Loading', key, 'from fast cache');
    // If you request something from the fast queue
    // we want to update the priority list so we don't
    // drop something from the fast cache that was recently used
    placeFirstInFastCache(fastCache[key]);
    return Promise.resolve(fastCache[key]);
  }
  const urls = getAllCachedUrls();
  if (urls && urls.indexOf(key) === -1) {
    return Promise.resolve(findInFastCache(url));
  }
  return (storage
    .getItem<CacheItem>(key)
    .then(
      item => {
        if (item) {
          addToFastCacheIfFullItem(item);
        }
        return item;
      },
      () => {
        // If the storage is full, we just act like we don't have anything
        // in the cache
        return null;
      },
    )
    .then((cacheItem: any) => {
      if (!cacheItem) {
        return findInFastCache(url);
      }
      return cacheItem;
    }) as any) as Promise<CacheItem>;
}

function findInFastCache(url: URLX) {
  for (let i = fastCacheUrls.length - 1; i >= 0; i--) {
    const partial = findIn(fastCache[fastCacheUrls[i]], url);
    if (partial) {
      return partial;
    }
  }
  return null;
}

function findIn(cacheItem: object, url: URLX): CacheItem | null {
  const relativeHref = relativeUrl(url);
  if (Array.isArray(cacheItem)) {
    for (const item of cacheItem) {
      if (isCacheItem(item) && relativeHref === item.url) {
        return item;
      }

      // A partial never contains cache items
      if (!isCacheItem(item) || item.$cache !== 'partial') {
        const deep = findIn(item, url);
        if (deep) {
          return deep;
        }
      }
    }
  } else if (cacheItem instanceof Object) {
    if (isCacheItem(cacheItem) && relativeHref === cacheItem.url) {
      return cacheItem;
    }

    // A partial never contains cache items
    if (!isCacheItem(cacheItem) || cacheItem.$cache !== 'partial') {
      const keys = Object.keys(cacheItem);
      for (const key of keys) {
        const item = cacheItem[key];
        if (isCacheItem(item) && relativeHref === item.url) {
          return item;
        }
        const deep = findIn(item, url);
        if (deep) {
          return deep;
        }
      }
    }
  }
  return null;
}

function placeFirstInFastCache(item: CacheItem) {
  if (isServiceWorker()) {
    return;
  }

  if (item.url) {
    fastCacheUrls = fastCacheUrls.filter(cachedUrl => cachedUrl !== item.url);
    fastCacheUrls.push(item.url);
  }
}

function addToFastCacheIfFullItem(item: CacheItem) {
  if (isServiceWorker()) {
    // We don't want an in-memory cache in the service worker, we only
    // want to load from the persistent database
    return;
  }
  if (item.$cache === 'full') {
    if (item.url) {
      if (fastCacheUrls.length > MAX_FAST_CACHE_SIZE) {
        const oldestUrlInFastCache = fastCacheUrls.shift();
        if (oldestUrlInFastCache) {
          console.info('Dropping', oldestUrlInFastCache, 'from fast cache');
          delete fastCache[oldestUrlInFastCache];
        }
      }
      placeFirstInFastCache(item);
      fastCache[item.url] = item;
    }
  }
}

export function setCacheItem(item: CacheItem, fastCacheOnly = false): Promise<any> {
  if (!supportAsyncStorage) {
    return Promise.resolve(null);
  }
  if (item.shouldCache === false) {
    console.info('Not caching ', item.url);
    return Promise.resolve(null);
  }
  addToFastCacheIfFullItem(item);

  if (fastCacheOnly) {
    return Promise.resolve(null);
  }

  return item.url
    ? storage.getItem<CacheItem>(item.url).then(existing => {
        if (!existing || existing.$cache === 'partial' || item.$cache === 'full') {
          onCacheSet(item.url);

          // We don't want to remove unserializable properties from the object
          // because the sender might use them after calling this method
          const serializable: CacheItem = {} as CacheItem;
          Object.keys(item).forEach(property => {
            // Because we can't serialize functions
            if (!(item[property] instanceof Function)) {
              serializable[property] = item[property];
            }
          });
          return storage
            .setItem(item.url, serializable)
            .catch(e => console.error('Error storing in item cache', item.url, e));
        } else {
          console.info('Not storing partial cache for ' + item.url + ' because there is already full content cached');
        }
        return Promise.resolve(null);
      })
    : Promise.reject('Invalid URL');
}

export function clearCacheItem(url: URLX) {
  const relativeHref = relativeUrl(url);
  const index = fastCacheUrls.findIndex(u => u === relativeHref);

  if (index !== -1) {
    fastCacheUrls.splice(index, 1);
    delete fastCache[relativeHref];
  }
  return storage.removeItem(relativeHref);
}

export function nukeCache() {
  Object.keys(fastCache).forEach(url => delete fastCache[url]);

  fastCacheUrls = [];
  return storage.clear().catch(() => null);
}
// This is used in _Splashscreen.html
if (isBrowser()) {
  window.nukeItemCache = nukeCache;
}

const urlsByDateKey = 'urls-by-date';
let flushTimer: number;
let pendingNewUrls: string[] = [];

function onCacheSet(href: string) {
  if (!pendingNewUrls.includes(href)) {
    pendingNewUrls.unshift(href);

    clearTimeout(flushTimer);
    flushTimer = setTimeout(saveCachedUrlsList, 100);
  }
}

function saveCachedUrlsList() {
  storage.getItem<string[]>(urlsByDateKey).then(urls => {
    urls = urls || [];
    urls = urls.filter(url => !pendingNewUrls.includes(url));

    urls = pendingNewUrls.concat(urls);
    pendingNewUrls = [];

    if (urls.length > maxCacheSize) {
      const urlsToRemove = urls.slice(maxCacheSize - 1);
      urls = urls.slice(0, maxCacheSize - 1);
      urlsToRemove.forEach(url => {
        storage.removeItem(url);
        console.info('Removing', url, 'from the item cache');
      });
    }

    allCachedUrls = urls;
    storage.setItem(urlsByDateKey, urls).then(() => {
      if (pendingNewUrls.length > 0) {
        clearTimeout(flushTimer);
        flushTimer = setTimeout(saveCachedUrlsList, 100);
      }
    });
  });
}
